import React from "react";
import type { ReactElement, ReactNode } from "react";
import { Skeleton, cn } from "@ndd/ui";
import { ExternalLink } from "lucide-react";
import {
  endOfWeekIso,
  formatLocalDate,
  isoDateToUtcMs,
  startOfWeekIso,
  todayIsoDate,
  utcMsToIsoDate,
} from "../../../../shared/local-date";
import { formValueLabel } from "../../../../shared/form-registry";
import {
  SESSION_RATING_LABELS,
  type SessionQuality,
} from "../../../../shared/session-quality";
import type {
  FormMeetingCadence,
  FormSnapshotData,
  FormType,
} from "../../../../shared/form-types";
import type { FormProfileData } from "./forms-profile-card";
import type { MeetingNoteData } from "./meeting-note-types";
import type { SessionRowData } from "./session-row";
import { Badge } from "../badge";
import {
  scorecardRatingDisplay,
  type ScorecardRatingBadgeVariant,
} from "../../lib/analysis-display";
import {
  THEMED_CARD,
  THEMED_DISPLAY_VALUE,
  THEMED_EYEBROW,
  THEMED_RULE,
} from "../../lib/always-look";

/*
 * The four-cell context row under the RD header (frame 97:2960). Every value
 * is derived from rows the sibling surfaces already read; the helpers below
 * touch only dates, counts, statuses, and the cadence word, never a client
 * name, summary, title, or note body. Relative imports and `import React` so
 * `tsx --test` can render the row.
 */

export const MISSING = "—";

export type ContextSurface = "meetings" | "forms" | { sessionId: string };

export interface ContextExternalTarget {
  target: "_blank";
  rel: "noopener noreferrer";
  "aria-label": string;
}

/** Renders the value of a linked cell; rd-detail supplies the router Link. */
export type RenderContextLink = (
  surface: ContextSurface,
  className: string,
  children: ReactNode,
  externalTarget?: ContextExternalTarget
) => ReactElement;

export interface ContextCell {
  label: string;
  /** Main line. `null` means the record is absent and a dash is drawn. */
  value: string | null;
  /** Muted word after the value ("recorded"). */
  valueSuffix?: string;
  /** Second line: the cadence or recency, or the plain reason when absent. */
  detail: string;
  /** Surface the value links to; absent-record cells never link. */
  linkTo?: ContextSurface;
  /** Optional pop-out link immediately after the detail date. */
  detailLink?: { surface: ContextSurface; externalTarget: ContextExternalTarget };
  /** True for a muted value with no data behind it ("Not enough sessions yet"). */
  placeholder?: boolean;
  /** Draws the value as a rating badge in the app's rating spectrum. */
  badge?: { variant: ScorecardRatingBadgeVariant };
  /** Draws the value in the warning colour (an overdue next meeting). */
  tone?: "warning";
}

export type ContextRowState<T> =
  | { status: "pending" }
  | { status: "error" }
  | { status: "ready"; data: T };

export type ContextMeetingNote = Pick<MeetingNoteData, "meetingDate">;
export type ContextSession = Pick<SessionRowData, "id" | "sessionDate">;
export type ContextForm = Pick<
  FormSnapshotData,
  "periodStartDate" | "status" | "formType"
>;
export type ContextProfile = Pick<FormProfileData, "meetingCadence"> | null;

const CADENCE_WORDS: Record<FormMeetingCadence, string> = {
  weekly: "Weekly",
  biweekly: "Biweekly",
  monthly: "Monthly",
  as_needed: "As needed",
};

const FORM_TYPE_WORDS: Record<FormType, string> = {
  weekly_form: "Weekly",
  monthly_checkpoint: "Monthly checkpoint",
};

const DAY_MS = 86_400_000;

function latestDate(dates: Array<string | null>): string | null {
  let latest: string | null = null;

  for (const date of dates) {
    if (date && (latest === null || date > latest)) latest = date;
  }

  return latest;
}

function cadenceLine(profile: ContextProfile): string {
  const cadence = profile?.meetingCadence;

  return cadence ? `${CADENCE_WORDS[cadence]} cadence` : "No cadence set";
}

/** "today", "1 day ago", "N days ago", or "N weeks ago" from an ISO date. */
export function recencyWord(isoDate: string, todayIso: string): string {
  const days = Math.max(
    0,
    Math.round((isoDateToUtcMs(todayIso) - isoDateToUtcMs(isoDate)) / DAY_MS)
  );
  if (days === 0) return "today";

  if (days < 14) return `${days} day${days === 1 ? "" : "s"} ago`;

  const weeks = Math.floor(days / 7);

  return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
}

export const NEXT_MEETING_LABEL = "Next meeting due";

/** A cadence the next meeting can be projected from ("As needed" cannot). */
export type ProjectedCadence = Exclude<FormMeetingCadence, "as_needed">;

/**
 * The next meeting's due date, projected from the last meeting's ISO date:
 * weekly adds 7 days, biweekly 14, and monthly lands on the same day of the
 * next month, or that month's last day when it is shorter (Jan 31 -> Feb 28).
 */
export function projectNextMeetingIso(
  lastMeetingIso: string,
  cadence: ProjectedCadence
): string {
  if (cadence !== "monthly") {
    const days = cadence === "weekly" ? 7 : 14;

    return utcMsToIsoDate(isoDateToUtcMs(lastMeetingIso) + days * DAY_MS);
  }

  const [year, month, day] = lastMeetingIso.split("-").map(Number);
  // `month` is 1-based, so as a 0-based index it already names the next month.
  const nextMonth = month ?? 1;
  const daysInNextMonth = new Date(
    Date.UTC(year ?? 1970, nextMonth + 1, 0)
  ).getUTCDate();

  return utcMsToIsoDate(
    Date.UTC(year ?? 1970, nextMonth, Math.min(day ?? 1, daysInNextMonth))
  );
}

/** "Thu Sep 3": weekday, short month, and day, with no comma and no year. */
export function meetingDateLabel(isoDate: string): string {
  const weekday = new Date(isoDateToUtcMs(isoDate)).toLocaleDateString("en-US", {
    weekday: "short",
    timeZone: "UTC",
  });

  return `${weekday} ${formatLocalDate(isoDate, "short")}`;
}

/**
 * Next meeting due: the most recent meeting note's date projected by the
 * profile cadence (NO-594). A projected date before today reads as overdue in
 * the warning colour. "As needed", or no cadence at all, has nothing to
 * project from, so the cell shows the last meeting instead.
 */
export function nextMeetingCell(
  notes: ContextMeetingNote[],
  profile: ContextProfile,
  todayIso: string
): ContextCell {
  const last = latestDate(notes.map((note) => note.meetingDate));

  if (!last) {
    return { label: NEXT_MEETING_LABEL, value: null, detail: "No meetings yet" };
  }

  const cadence = profile?.meetingCadence ?? null;

  if (cadence === null || cadence === "as_needed") {
    return {
      label: "Last meeting",
      value: meetingDateLabel(last),
      detail: cadenceLine(profile),
      linkTo: "meetings",
    };
  }

  const due = projectNextMeetingIso(last, cadence);
  const overdue = due < todayIso;

  return {
    label: NEXT_MEETING_LABEL,
    value: overdue ? `Overdue since ${meetingDateLabel(due)}` : meetingDateLabel(due),
    detail: cadenceLine(profile),
    linkTo: "meetings",
    tone: overdue ? "warning" : undefined,
  };
}

/**
 * Sessions this week: a count over the Monday-Sunday week containing today.
 * An RD with sessions but none this week reads 0 (a real count); an RD with
 * no dated sessions at all reads as absent. Null dates are skipped.
 */
export function sessionsThisWeekCell(
  sessions: ContextSession[],
  todayIso: string
): ContextCell {
  const dated = sessions.filter(
    (session): session is ContextSession & { sessionDate: string } =>
      session.sessionDate !== null
  );
  const last = dated.reduce<(typeof dated)[number] | null>(
    (latest, session) =>
      latest === null || session.sessionDate > latest.sessionDate ? session : latest,
    null
  );

  if (!last) {
    return { label: "Sessions this week", value: null, detail: "No sessions recorded" };
  }

  const start = startOfWeekIso(todayIso);
  const end = endOfWeekIso(todayIso);
  const count = dated.filter(
    ({ sessionDate }) => sessionDate >= start && sessionDate <= end
  ).length;

  return {
    label: "Sessions this week",
    value: String(count),
    valueSuffix: "recorded",
    detail: `Last: ${formatLocalDate(last.sessionDate, "short")}`,
    detailLink: {
      surface: { sessionId: last.id },
      externalTarget: {
        target: "_blank",
        rel: "noopener noreferrer",
        "aria-label": "Open last session in a new tab",
      },
    },
  };
}

const TREND_WORDS = { up: "Up from", down: "Down from" } as const;

/**
 * Session quality (NO-737): the lower median of the RD's last 5 counted
 * sessions' ratings as a badge, with the trend against the 5 before them. The
 * server sends no trend until 10 sessions count, and no summary below 3.
 */
export function sessionQualityCell(
  quality: SessionQuality | null | undefined
): ContextCell {
  if (!quality) {
    return {
      label: "Session quality",
      value: "Not enough sessions yet",
      detail: "Scorecard rating over the last 5",
      placeholder: true,
    };
  }

  const { trend } = quality;
  let detail = `Scorecard rating over the last ${quality.sampleSize}`;
  if (trend?.direction === "steady") detail = "Steady";
  else if (trend)
    detail = `${TREND_WORDS[trend.direction]} ${SESSION_RATING_LABELS[trend.previousRating]}`;

  return {
    label: "Session quality",
    value: SESSION_RATING_LABELS[quality.rating],
    detail,
    badge: { variant: scorecardRatingDisplay[quality.rating].badgeVariant },
  };
}

/** Latest functional form: the newest snapshot's date and status, with its cadence and recency. */
export function latestFormCell(forms: ContextForm[], todayIso: string): ContextCell {
  const latest = forms.reduce<ContextForm | null>(
    (best, form) =>
      best === null || form.periodStartDate > best.periodStartDate ? form : best,
    null
  );
  if (!latest)
    return { label: "Latest functional form", value: null, detail: "No forms yet" };

  const date = formatLocalDate(latest.periodStartDate, "short");
  const status = formValueLabel(latest.status);

  return {
    label: "Latest functional form",
    value: status ? `${date} · ${status}` : date,
    detail: `${FORM_TYPE_WORDS[latest.formType]} · ${recencyWord(latest.periodStartDate, todayIso)}`,
    linkTo: "forms",
  };
}

// Under the always theme the row is a white card with mono eyebrows and its
// values in display type (NO-792); the brand classes leave the classic row as
// it was.
const ROW_CLASS = cn(
  "grid grid-cols-2 overflow-hidden rounded-xl border border-border bg-card shadow-sm md:grid-cols-4",
  THEMED_CARD
);
const CELL_CLASS = cn(
  "flex min-h-[74px] flex-col gap-1 px-[14px] py-3 border-border [&:nth-child(n+2)]:border-t md:[&:nth-child(n+2)]:border-t-0 md:[&:nth-child(n+2)]:border-l",
  THEMED_RULE
);
const LABEL_CLASS = cn(
  "text-caption font-semibold uppercase tracking-[0.06em] text-muted-foreground",
  THEMED_EYEBROW
);
const DD_CLASS = cn("flex flex-col gap-1", THEMED_DISPLAY_VALUE);
const VALUE_CLASS =
  "min-w-0 truncate text-reading font-semibold leading-5 text-foreground";
const LINK_CLASS = "text-primary underline-offset-4 hover:underline";
const DETAIL_CLASS = "text-xs leading-4 text-muted-foreground";

function CellValue({
  cell,
  renderLink,
}: {
  cell: ContextCell;
  renderLink: RenderContextLink;
}): ReactElement {
  if (cell.value === null) {
    return <span className={VALUE_CLASS}>{MISSING}</span>;
  }

  const toneClass = cell.tone === "warning" && "text-warning";

  if (cell.linkTo) {
    return renderLink(cell.linkTo, cn(VALUE_CLASS, LINK_CLASS, toneClass), cell.value);
  }

  if (cell.badge) {
    return (
      <Badge variant={cell.badge.variant} size="md" title={cell.value}>
        {cell.value}
      </Badge>
    );
  }

  return (
    <span
      className={cn(
        VALUE_CLASS,
        cell.placeholder && "text-muted-foreground",
        toneClass
      )}
      title={cell.value}
    >
      {cell.value}
    </span>
  );
}

function Cell({
  cell,
  state,
  renderLink,
}: {
  cell: ContextCell;
  state: "pending" | "error" | "ready";
  renderLink: RenderContextLink;
}): ReactElement {
  const shown: ContextCell =
    state === "error"
      ? { label: cell.label, value: null, detail: "Couldn't load" }
      : cell;

  return (
    <div className={CELL_CLASS} data-testid="context-cell">
      <dt className={LABEL_CLASS}>{cell.label}</dt>
      {state === "pending" ? (
        <dd className={DD_CLASS} aria-busy="true">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-4 w-20" />
        </dd>
      ) : (
        <dd className={DD_CLASS}>
          <span
            className="flex min-w-0 items-baseline gap-1.5"
            data-testid="context-value"
          >
            <CellValue cell={shown} renderLink={renderLink} />
            {shown.valueSuffix && (
              <span className="text-reading leading-5 text-muted-foreground">
                {shown.valueSuffix}
              </span>
            )}
          </span>
          <span className={DETAIL_CLASS}>
            {shown.detail}
            {shown.detailLink &&
              renderLink(
                shown.detailLink.surface,
                "ml-1 inline-flex cursor-pointer align-middle text-primary",
                <ExternalLink className="h-3 w-3" aria-hidden="true" />,
                shown.detailLink.externalTarget
              )}
          </span>
        </dd>
      )}
    </div>
  );
}

function cellState(
  states: Array<ContextRowState<unknown>>
): "pending" | "error" | "ready" {
  if (states.some((state) => state.status === "pending")) return "pending";

  if (states.some((state) => state.status === "error")) return "error";

  return "ready";
}

function ready<T>(state: ContextRowState<T>, fallback: T): T {
  return state.status === "ready" ? state.data : fallback;
}

/** The context row: Next meeting due, Sessions this week, Session quality, Latest functional form. */
export function SnapshotContextRow({
  meetingNotes,
  sessions,
  forms,
  profile,
  sessionQuality,
  renderLink,
  todayIso = todayIsoDate(),
}: {
  meetingNotes: ContextRowState<ContextMeetingNote[]>;
  sessions: ContextRowState<ContextSession[]>;
  forms: ContextRowState<ContextForm[]>;
  profile: ContextRowState<ContextProfile>;
  /** From `GET /api/rds/:id`, already loaded when the row renders. */
  sessionQuality: SessionQuality | null | undefined;
  renderLink: RenderContextLink;
  /** Injected by tests; defaults to the viewer's local calendar date. */
  todayIso?: string;
}): ReactElement {
  const meetingState = cellState([meetingNotes, profile]);
  const cells: Array<{ cell: ContextCell; state: "pending" | "error" | "ready" }> = [
    {
      // Until both lists load the cadence is unknown, so keep the default label.
      cell:
        meetingState === "ready"
          ? nextMeetingCell(ready(meetingNotes, []), ready(profile, null), todayIso)
          : { label: NEXT_MEETING_LABEL, value: null, detail: "" },
      state: meetingState,
    },
    {
      cell: sessionsThisWeekCell(ready(sessions, []), todayIso),
      state: cellState([sessions]),
    },
    { cell: sessionQualityCell(sessionQuality), state: "ready" },
    { cell: latestFormCell(ready(forms, []), todayIso), state: cellState([forms]) },
  ];

  return (
    <dl
      className={ROW_CLASS}
      aria-label="RD context"
      data-testid="snapshot-context-row"
    >
      {cells.map(({ cell, state }) => (
        <Cell key={cell.label} cell={cell} state={state} renderLink={renderLink} />
      ))}
    </dl>
  );
}
